import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Question, Answer } from '../types';

interface QuizContainerProps {
  questions: Question[];
  currentQuestion: number;
  answers: Record<number, Answer>;
  onAnswer: (questionId: number, answer: Answer) => void;
  onNext: () => void;
  onPrev: () => void;
}

const QuizContainer: React.FC<QuizContainerProps> = ({
  questions,
  currentQuestion,
  answers,
  onAnswer,
  onNext,
  onPrev
}) => {
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleSingleAnswer = (optionId: string) => {
    onAnswer(question.id, optionId);
  };

  const handleMultipleAnswer = (optionId: string) => {
    const currentAnswers = answers[question.id] as string[] || [];
    const newAnswers = currentAnswers.includes(optionId)
      ? currentAnswers.filter(id => id !== optionId)
      : [...currentAnswers, optionId];
    onAnswer(question.id, newAnswers);
  };

  const isAnswered = () => {
    const answer = answers[question.id];
    if (!answer) return false;
    
    if (question.type === 'single') {
      return !!answer;
    } else {
      return (answer as string[]).length > 0;
    }
  };

  const isLastQuestion = currentQuestion === questions.length - 1;

  return (
    <div className="max-w-3xl mx-auto px-4">
      <div className="bg-white rounded-2xl p-8 shadow-lg relative min-h-[500px] flex flex-col mt-20 md:mt-24">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 rounded-t-2xl overflow-hidden">
          <div 
            className="h-full bg-[#7AEA53] transition-all duration-500 ease-out" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <div className="pt-4 pb-6">
          <div className="text-sm text-[#2E6173] mb-2">
            Pergunta <span>{currentQuestion + 1}</span> de <span>{questions.length}</span>
          </div>
          <h2 className="text-2xl font-semibold text-[#103541]">{question.title}</h2>
        </div>
        
        <div className="space-y-3 mb-8 flex-grow">
          {question.type === 'single' && question.options.map(option => (
            <div
              key={option.id}
              className={`p-4 rounded-xl cursor-pointer border-2 transition-all ${
                answers[question.id] === option.id
                  ? 'border-[#7AEA53] bg-[#f9fff5]'
                  : 'border-gray-200 bg-white hover:border-[#7AEA53]'
              }`}
              onClick={() => handleSingleAnswer(option.id)}
            >
              {option.label}
            </div>
          ))}
          
          {question.type === 'multiple' && question.options.map(option => {
            const isSelected = (answers[question.id] as string[] || []).includes(option.id);
            return (
              <div
                key={option.id}
                className={`flex items-center p-4 rounded-xl cursor-pointer border-2 transition-all ${
                  isSelected
                    ? 'border-[#7AEA53] bg-[#f9fff5]'
                    : 'border-gray-200 bg-white hover:border-[#7AEA53]'
                }`}
                onClick={() => handleMultipleAnswer(option.id)}
              >
                <div className={`h-5 w-5 rounded flex items-center justify-center mr-3 border-2 ${
                  isSelected 
                    ? 'border-[#7AEA53] bg-[#7AEA53] text-white' 
                    : 'border-gray-300'
                }`}>
                  {isSelected && <span className="text-xs">✓</span>}
                </div>
                <span>{option.label}</span>
              </div>
            );
          })}
        </div>
        
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            className="flex items-center gap-2 py-3 px-5 rounded-lg border border-gray-200 text-[#2E6173] font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onPrev}
            disabled={currentQuestion === 0}
          >
            <ArrowLeft size={16} />
            Voltar
          </button>
          
          <button
            className="flex items-center gap-2 py-3 px-6 bg-[#7AEA53] text-white font-medium rounded-lg hover:bg-[#6ad142] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex-1 justify-center ml-3"
            onClick={onNext}
            disabled={!isAnswered()}
          >
            {isLastQuestion ? 'Ver Resultado' : 'Próxima'}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizContainer;